export type PriceCurrency = 'COP' | 'USD';

export interface PriceParts {
  currencyCode: PriceCurrency;
  amount: string;
}

// Group separators are a local convention, not a preference: a Colombian reader
// expects 234.600, a US reader expects 1,200. Formatting COP with en-US produced
// "COP 234,600", which is both wrong for the audience and the longest possible
// rendering of the number.
const CURRENCY_LOCALE: Record<PriceCurrency, string> = {
  COP: 'es-CO',
  USD: 'en-US',
};

/**
 * Splits a price into its currency code and grouped amount.
 *
 * They are returned separately so the card can render the code at a smaller
 * size than the number — COP amounts are an order of magnitude longer than USD
 * ones, and a single `Intl` currency string cannot be styled in two sizes.
 */
export function formatPriceParts(amountInMinor: number, currencyCode: PriceCurrency): PriceParts {
  const amount = new Intl.NumberFormat(CURRENCY_LOCALE[currencyCode] ?? 'en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amountInMinor / 100);

  return { currencyCode, amount };
}

// Below this the number stops reading as the card's headline.
const MIN_FONT_SIZE = 20;

// Prices render in GeistMono. Every glyph in a monospace face has the same
// advance, so width is exactly `length * ratio * fontSize` — measured at 0.6021
// em/char in a browser, rounded up here for margin.
const MONO_ADVANCE_RATIO = 0.61;

// Horizontal room left for the number inside a desktop card: the 1280px
// container splits into five 243px cards, less 24px padding a side, leaving
// 195px of content. The currency code sits on its own line above, so only the
// "/mo" suffix and one gap compete with the number — without that, a 9-digit
// COP amount was forced down to 20px against USD's 36px.
//
// Mobile cards are wider, so this desktop figure is the binding constraint.
// Held ~5% under the measured limit: at the theoretical maximum the row filled
// 193px of a 193px card, so a font falling back to a wider face would overflow.
export const PRICE_NUMBER_BUDGET_PX = 158;

/**
 * Picks the largest font size that keeps `amount` on one line.
 *
 * This exists because `adjustsFontSizeToFit` is an iOS-only prop that
 * react-native-web silently ignores: paired with `numberOfLines={1}`, a long
 * amount is clipped to an ellipsis rather than shrunk, which is how
 * "COP 1.016.600" reached production as "COP 1…".
 *
 * The size is solved from the available width rather than picked from length
 * buckets — buckets were tried first and still overflowed, because a 7-digit
 * COP amount is roughly three times the width of "$69" and the thresholds were
 * guessed instead of measured. `baseSize` is an upper bound, not a scale.
 */
export function priceFontSize(
  amount: string,
  baseSize: number,
  budgetPx: number = PRICE_NUMBER_BUDGET_PX,
): number {
  const fitted = Math.floor(budgetPx / Math.max(amount.length, 1) / MONO_ADVANCE_RATIO);

  return Math.max(Math.min(fitted, baseSize), Math.min(MIN_FONT_SIZE, baseSize));
}

/**
 * The one size every card in the row should use: the smallest any of them
 * needs.
 *
 * Sizing each card independently makes the table look ragged — under COP the
 * Starter number would sit at 26px beside a 20px Business number. A price table
 * is a comparison surface, so the type has to be uniform across it.
 */
export function sharedPriceFontSize(
  amounts: readonly string[],
  baseSize: number,
  budgetPx: number = PRICE_NUMBER_BUDGET_PX,
): number {
  if (amounts.length === 0) {
    return baseSize;
  }

  return Math.min(...amounts.map((amount) => priceFontSize(amount, baseSize, budgetPx)));
}
